/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial creation of the VersioningForm component.
 *
 * -----
 * SECURITY_RISK: LOW - This component handles user input for versioning records.
 *                      It does not process sensitive data directly or interact with external systems.
 * INTENT: Provide a reusable form for creating and editing Versioning records,
 *         ensuring data integrity and a clear user experience.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure input validation is robust and
 *                        that the form state correctly reflects submission status.
 * LLM_CONFIDENCE: 100% - Standard form component with state management and input handling.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Versioning, VersioningCreate, VersioningUpdate } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Spinner } from './ui/spinner.tsx';

interface VersioningFormProps {
  initialData?: Versioning | null;
  onSubmit: (data: VersioningCreate | VersioningUpdate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const defaultFormData: VersioningCreate = {
  tag: '',
  description: '',
  created_by: 'tester', // Default value for created_by
};

const VersioningForm: React.FC<VersioningFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<VersioningCreate | VersioningUpdate>(
    initialData
      ? {
          tag: initialData.tag,
          description: initialData.description,
          updated_by: initialData.updated_by || 'tester', // Use existing or default
        }
      : defaultFormData
  );
  const [tagError, setTagError] = useState<string | null>(null); // State for tag validation error

  useEffect(() => {
    if (initialData) {
      setFormData({
        tag: initialData.tag,
        description: initialData.description,
        updated_by: initialData.updated_by || 'tester',
      });
    } else {
      setFormData(defaultFormData);
    }
    setTagError(null); // Clear error on initial data change
  }, [initialData]);

  const validateTag = useCallback((tag: string) => {
    // JavaScript equivalent regex: /^[a-z0-9_]+$/
    const regex = /^[a-z0-9_]+$/;
    if (tag === '') {
      setTagError(null); // No error if empty (handled by 'required' attribute or later logic)
      return true;
    }
    if (!regex.test(tag)) {
      setTagError('Tag can only contain lowercase letters, numbers, and underscores.');
      return false;
    }
    if (tag.toLowerCase() == 'main') {
      setTagError('Please select a different tag.');
      return false;
    }
    setTagError(null);
    return true;
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [id]: value,
      };
      if (id === 'tag') {
        validateTag(value);
      }
      return newFormData;
    });
  }, [validateTag]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    // Perform final validation before submission
    if (!initialData && !validateTag(formData.tag)) { // Only validate tag for new versions
      return; // Prevent submission if tag is invalid
    }

    onSubmit(formData);
  }, [formData, onSubmit, initialData, validateTag]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!initialData && (
        <div>
          <Label htmlFor="tag" className="block text-sm font-medium text-foreground mb-1">Tag</Label>
          <Input
            id="tag"
            type="text"
            required
            value={formData.tag || ''}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          <p className="text-xs text-muted-foreground italic mt-1">
            Allowed characters: lowercase letters (a-z), numbers (0-9), and underscores (_).
          </p>
          {tagError && <p className="text-destructive text-sm mt-1">{tagError}</p>}
        </div>
      )}
      <div>
        <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">Description</Label>
        <Input
          id="description"
          type="text"
          required
          value={formData.description || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isSubmitting || (tagError !== null)}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default VersioningForm;